import { Title, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconBook,
  IconCpu,
  IconFileInvoice,
  IconListCheck,
  IconMail,
  IconMailbox,
  IconMessageCircle,
  IconNetwork,
  IconPackages,
  IconReceipt,
  IconRobot,
  IconSend,
  IconSettings,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useState } from 'react';
// AppSidebar itself is domain-coupled (Redux domain state, routed feature
// links), so only its stylesheet is shared — the super admin rail then looks
// and behaves like the one on a domain page without touching it.
import classes from '../AppSidebar.module.css';

const superAdminSections = [
  {
    id: 'bots',
    icon: IconRobot,
    label: 'Bots',
    items: [
      { id: 'botClassification', name: 'Bot Classification', icon: IconListCheck },
      { id: 'botIpRanges', name: 'Bot IP Ranges', icon: IconNetwork },
    ],
  },
  {
    id: 'billing',
    icon: IconReceipt,
    label: 'Billing',
    items: [
      { id: 'invoices', name: 'Invoices', icon: IconFileInvoice },
      { id: 'partners', name: 'Partners', icon: IconUsersGroup },
    ],
  },
  {
    id: 'email',
    icon: IconMail,
    label: 'Email',
    items: [
      { id: 'operationalEmails', name: 'Operational Emails', icon: IconMail },
      { id: 'operationalEmailEvents', name: 'Operational Email Events', icon: IconMailbox },
      { id: 'emailTestSend', name: 'Email Test Send', icon: IconSend },
    ],
  },
  {
    id: 'ai',
    icon: IconMessageCircle,
    label: 'AI',
    items: [
      { id: 'chatBotSettings', name: 'Chat Bot Settings', icon: IconSettings },
      { id: 'knowledgeBase', name: 'Knowledge Base', icon: IconBook },
      { id: 'aiModels', name: 'AI Providers & Models', icon: IconCpu },
    ],
  },
  {
    id: 'feeds',
    icon: IconPackages,
    label: 'Feeds',
    items: [{ id: 'feedBandit', name: 'Feed Bandit', icon: IconPackages }],
  },
];

const sectionOf = (itemId) => superAdminSections.find((section) => section.items.some((item) => item.id === itemId));

const SuperAdminSidebar = ({ value, onChange }) => {
  // While the panel is open the rail previews whichever section the pointer is
  // over; closed, it falls back to the section holding the visible one.
  const [previewSectionId, setPreviewSectionId] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const shownSection = superAdminSections.find((section) => section.id === previewSectionId) ?? sectionOf(value);

  const showSection = (sectionId) => {
    setPreviewSectionId(sectionId);
    setIsPanelOpen(true);
  };

  const closePanel = () => {
    setPreviewSectionId(null);
    setIsPanelOpen(false);
  };

  const selectItem = (itemId) => {
    onChange(itemId);
    closePanel();
  };

  return (
    <div className={classes.sidebarContainer} onMouseLeave={closePanel}>
      <nav className={classes.navbar}>
        <div className={classes.wrapper}>
          <div className={classes.aside}>
            {superAdminSections.map((section) => (
              <Tooltip
                label={section.label}
                position="right"
                withArrow
                transitionProps={{ duration: 0 }}
                key={section.id}
                disabled={isPanelOpen}
              >
                <UnstyledButton
                  onClick={() => showSection(section.id)}
                  onMouseEnter={() => showSection(section.id)}
                  className={classes.mainLink}
                  data-active={section.id === shownSection.id || undefined}
                  aria-label={section.label}
                >
                  <section.icon size={22} stroke={1.5} />
                </UnstyledButton>
              </Tooltip>
            ))}
          </div>
        </div>
      </nav>
      {isPanelOpen && (
        <div className={classes.main}>
          <Title order={5} className={classes.title}>
            {shownSection.label}
          </Title>
          <div className={classes.linksContainer}>
            {shownSection.items.map((item) => (
              <UnstyledButton
                key={item.id}
                // A button shrinks to fit even as a flex container, where the
                // domain sidebar's rows are anchors and fill on their own.
                w="100%"
                className={classes.link}
                data-active={item.id === value || undefined}
                onClick={() => selectItem(item.id)}
              >
                <item.icon size={18} />
                {item.name}
              </UnstyledButton>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminSidebar;
